import { timeToXMedia } from '../../components/StockChart/chartMarkersPrimitiveUtils';
import { resolveStopPrice, resolveTakeProfitPrice } from '../engine/fillModel';

/** Vertical hairline at the replay playhead. */
export class PlayheadPrimitive {
  constructor(color = '#6366F1') {
    this._time = null;
    this._color = color;
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
    const self = this;
    this._paneView = {
      zOrder: () => 'top',
      update: () => {},
      renderer: () => ({
        draw(target) {
          if (!self._chart || self._time == null) return;
          const x = timeToXMedia(self._chart, self._time);
          if (x == null) return;
          target.useBitmapCoordinateSpace((scope) => {
            const ctx = scope.context;
            const xPx = Math.round(x * scope.horizontalPixelRatio);
            ctx.strokeStyle = self._color;
            ctx.lineWidth = Math.max(1, scope.horizontalPixelRatio);
            ctx.globalAlpha = 0.5;
            ctx.setLineDash([4 * scope.verticalPixelRatio, 3 * scope.verticalPixelRatio]);
            ctx.beginPath();
            ctx.moveTo(xPx, 0);
            ctx.lineTo(xPx, scope.bitmapSize.height);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.globalAlpha = 1;
          });
        },
      }),
    };
  }

  setPlayhead(time, color) {
    this._time = time ?? null;
    if (color) this._color = color;
    this._requestUpdate?.();
  }

  attached(param) {
    this._chart = param.chart;
    this._series = param.series;
    this._requestUpdate = param.requestUpdate;
  }

  detached() {
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
  }

  updateAllViews() {
    this._paneView.update();
  }

  paneViews() {
    return [this._paneView];
  }
}

/** FVG rectangles from engine `fairValueGaps` series (aligned to bar index). */
export class FvgBoxesPrimitive {
  constructor() {
    this._boxes = [];
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
    const self = this;
    this._paneView = {
      zOrder: () => 'bottom',
      update: () => {},
      renderer: () => ({
        draw(target) {
          if (!self._chart || !self._series || !self._boxes.length) return;
          const timeScale = self._chart.timeScale();
          target.useBitmapCoordinateSpace((scope) => {
            const ctx = scope.context;
            const hRatio = scope.horizontalPixelRatio;
            const vRatio = scope.verticalPixelRatio;
            for (const box of self._boxes) {
              const x1 = timeScale.logicalToCoordinate(box.startLogical);
              const x2 = timeScale.logicalToCoordinate(box.endLogical);
              const y1 = self._series.priceToCoordinate(box.topPrice);
              const y2 = self._series.priceToCoordinate(box.bottomPrice);
              if (x1 == null || x2 == null || y1 == null || y2 == null) continue;
              const xMin = Math.min(x1, x2) * hRatio;
              const xMax = Math.max(x1, x2) * hRatio;
              const yMin = Math.min(y1, y2) * vRatio;
              const yMax = Math.max(y1, y2) * vRatio;
              ctx.fillStyle = box.bullish ? 'rgba(7, 203, 163, 0.16)' : 'rgba(247, 50, 99, 0.16)';
              ctx.fillRect(xMin, yMin, Math.max(1, xMax - xMin), Math.max(1, yMax - yMin));
            }
          });
        },
      }),
    };
  }

  setBoxes(boxes) {
    this._boxes = Array.isArray(boxes) ? boxes : [];
    this._requestUpdate?.();
  }

  attached(param) {
    this._chart = param.chart;
    this._series = param.series;
    this._requestUpdate = param.requestUpdate;
  }

  detached() {
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
  }

  updateAllViews() {
    this._paneView.update();
  }

  paneViews() {
    return [this._paneView];
  }
}

const SESSION_PURPLE = '#818CF8';

/** Session high/low as a box around the candles in that session, not a line that continues after. */
export class SessionBoxesPrimitive {
  constructor() {
    this._boxes = [];
    this._fill = 'rgba(129, 140, 248, 0.14)';
    this._stroke = 'rgba(129, 140, 248, 0.55)';
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
    const self = this;
    this._paneView = {
      zOrder: () => 'bottom',
      update: () => {},
      renderer: () => ({
        draw(target) {
          if (!self._chart || !self._series || !self._boxes.length) return;
          const timeScale = self._chart.timeScale();
          const spacing = Number(timeScale.options?.().barSpacing) || 6;
          target.useBitmapCoordinateSpace((scope) => {
            const ctx = scope.context;
            const hRatio = scope.horizontalPixelRatio;
            const vRatio = scope.verticalPixelRatio;
            const pad = (spacing / 2) * hRatio;
            for (const box of self._boxes) {
              const x1 = timeScale.logicalToCoordinate(box.startLogical);
              const x2 = timeScale.logicalToCoordinate(box.endLogical);
              const y1 = self._series.priceToCoordinate(box.highPrice);
              const y2 = self._series.priceToCoordinate(box.lowPrice);
              if (x1 == null || x2 == null || y1 == null || y2 == null) continue;
              const left = Math.min(x1, x2) * hRatio - pad;
              const right = Math.max(x1, x2) * hRatio + pad;
              const top = Math.min(y1, y2) * vRatio;
              const bottom = Math.max(y1, y2) * vRatio;
              const width = Math.max(1, right - left);
              const height = Math.max(1, bottom - top);
              ctx.fillStyle = self._fill;
              ctx.fillRect(left, top, width, height);
              ctx.strokeStyle = self._stroke;
              ctx.lineWidth = Math.max(1, hRatio);
              ctx.strokeRect(left, top, width, height);
            }
          });
        },
      }),
    };
  }

  setBoxes(boxes, color = SESSION_PURPLE) {
    this._boxes = Array.isArray(boxes) ? boxes : [];
    this._fill = withAlpha(color, 0.14);
    this._stroke = withAlpha(color, 0.55);
    this._requestUpdate?.();
  }

  attached(param) {
    this._chart = param.chart;
    this._series = param.series;
    this._requestUpdate = param.requestUpdate;
  }

  detached() {
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
  }

  updateAllViews() {
    this._paneView.update();
  }

  paneViews() {
    return [this._paneView];
  }
}

/** Small text labels for TP/SL fills (the 13px B/S circles cannot fit "TP 1 hit"). */
export class ExecutionLabelsPrimitive {
  constructor() {
    this._labels = [];
    this._bg = '#0C0F19';
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
    const self = this;
    this._paneView = {
      zOrder: () => 'top',
      update: () => {},
      renderer: () => ({
        draw(target) {
          if (!self._chart || !self._series || !self._labels.length) return;
          target.useMediaCoordinateSpace(({ context: ctx }) => {
            for (const label of self._labels) {
              const x = timeToXMedia(self._chart, label.time);
              const y = self._series.priceToCoordinate(label.price);
              if (x == null || y == null) continue;
              ctx.beginPath();
              ctx.arc(x, y, 2.5, 0, Math.PI * 2);
              ctx.fillStyle = label.color;
              ctx.fill();
              ctx.font = '500 9px system-ui, -apple-system, sans-serif';
              ctx.textAlign = 'left';
              ctx.textBaseline = 'middle';
              const text = label.text;
              const tw = ctx.measureText(text).width;
              const tx = x + 6;
              const th = 11;
              ctx.fillStyle = withAlpha(self._bg, 0.82);
              ctx.fillRect(tx - 2, y - th / 2, tw + 4, th);
              ctx.fillStyle = label.color;
              ctx.fillText(text, tx, y);
            }
          });
        },
      }),
    };
  }

  setLabels(labels, bg) {
    this._labels = Array.isArray(labels) ? labels : [];
    if (bg) this._bg = bg;
    this._requestUpdate?.();
  }

  attached(param) {
    this._chart = param.chart;
    this._series = param.series;
    this._requestUpdate = param.requestUpdate;
  }

  detached() {
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
  }

  updateAllViews() {
    this._paneView.update();
  }

  paneViews() {
    return [this._paneView];
  }
}

function withAlpha(color, alpha) {
  if (!color || typeof color !== 'string') return `rgba(127,127,127,${alpha})`;
  if (color.startsWith('rgba(')) return color.replace(/rgba\(([^)]+)\)/, (_, inner) => {
    const parts = inner.split(',').map((p) => p.trim());
    return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
  });
  if (color.startsWith('rgb(')) return color.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);
  const hex = color.replace('#', '');
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  if (full.length < 6) return `rgba(127,127,127,${alpha})`;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if (![r, g, b].every(Number.isFinite)) return `rgba(127,127,127,${alpha})`;
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Bracket geometry for a focused trade: spans entry → exit (or playhead if still open).
 */
export function buildTradeBracket(trade, playhead) {
  if (!trade || trade.entryIndex == null || trade.entryIndex > playhead) return null;
  if (!Number.isFinite(trade.entryPrice)) return null;
  const closed = trade.exitIndex != null && trade.exitIndex <= playhead;
  const endIndex = closed ? trade.exitIndex : playhead;
  const takeProfits = (trade.takeProfits || [])
    .filter((tp) => Number.isFinite(tp.price))
    .map((tp, i) => ({
      price: tp.price,
      label: `TP ${i + 1}`,
      filled: tp.filledIndex != null && tp.filledIndex <= playhead,
    }));
  return {
    startLogical: trade.entryIndex,
    endLogical: Math.max(trade.entryIndex, endIndex),
    entryPrice: trade.entryPrice,
    stopPrice: Number.isFinite(trade.stopPrice) ? trade.stopPrice : null,
    takeProfits,
    side: trade.side === 'short' ? 'short' : 'long',
    pending: Boolean(trade.pending),
  };
}

export function previewTradeFromWorkingOrder(order) {
  if (!order || order.type === 'exit') return null;
  if (!Number.isFinite(order.placedIndex)) return null;
  const side = order.side === 'short' ? 'short' : 'long';
  const entryPrice = Number.isFinite(Number(order.limitPrice)) ? Number(order.limitPrice) : null;
  if (!Number.isFinite(entryPrice)) return null;
  const stopPrice = resolveStopPrice(side, entryPrice, order);
  const rValue = Number.isFinite(stopPrice) ? Math.abs(entryPrice - stopPrice) : null;
  const takeProfits = (order.takeProfits || []).map((tp, i) => ({
    id: tp.id || `tp${i + 1}`,
    price: Number.isFinite(tp.price)
      ? tp.price
      : resolveTakeProfitPrice(side, entryPrice, rValue, Number(tp.rMultiple) || (i + 1)),
    filledIndex: null,
  }));
  return {
    entryIndex: order.placedIndex,
    exitIndex: null,
    entryPrice,
    stopPrice,
    side,
    takeProfits,
    pending: true,
  };
}

/** TradingView-style long/short position box for the selected (or open) trade. */
export class TradeBracketPrimitive {
  constructor() {
    this._bracket = null;
    this._colors = {
      success: '#07CBA3',
      danger: '#F73263',
      text: '#E8EDF4',
      textMuted: '#7B8A9E',
      bg: '#0C0F19',
    };
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
    const self = this;

    const renderer = () => ({
      draw(target) {
        const bracket = self._bracket;
        if (!self._chart || !self._series || !bracket) return;
        const timeScale = self._chart.timeScale();
        const x1 = timeScale.logicalToCoordinate(bracket.startLogical);
        const x2 = timeScale.logicalToCoordinate(bracket.endLogical);
        if (x1 == null || x2 == null) return;
        const spacing = Number(timeScale.options?.().barSpacing) || 6;
        const yEntry = self._series.priceToCoordinate(bracket.entryPrice);
        const yStop = Number.isFinite(bracket.stopPrice)
          ? self._series.priceToCoordinate(bracket.stopPrice)
          : null;
        const tpYs = (bracket.takeProfits || []).map((tp) => ({
          ...tp,
          y: self._series.priceToCoordinate(tp.price),
        }));
        if (yEntry == null) return;

        target.useBitmapCoordinateSpace((scope) => {
          const ctx = scope.context;
          const hRatio = scope.horizontalPixelRatio;
          const vRatio = scope.verticalPixelRatio;
          const pad = (spacing / 2) * hRatio;
          const left = Math.min(x1, x2) * hRatio - pad;
          const right = Math.max(x1, x2) * hRatio + pad;
          const width = Math.max(2 * hRatio, right - left);
          const yE = yEntry * vRatio;
          const success = self._colors.success;
          const danger = self._colors.danger;

          const fillBand = (yA, yB, fill) => {
            if (yA == null || yB == null) return;
            const top = Math.min(yA, yB);
            const h = Math.max(1, Math.abs(yB - yA));
            ctx.fillStyle = fill;
            ctx.fillRect(left, top, width, h);
          };

          if (yStop != null) {
            fillBand(yE, yStop * vRatio, withAlpha(danger, 0.16));
          }
          const farthestTp = tpYs.reduce((best, tp) => {
            if (tp.y == null) return best;
            if (!best || Math.abs(tp.y - yEntry) > Math.abs(best.y - yEntry)) return tp;
            return best;
          }, null);
          if (farthestTp?.y != null) {
            fillBand(yE, farthestTp.y * vRatio, withAlpha(success, 0.14));
          }

          const strokeLevel = (y, color, dashed = false) => {
            if (y == null) return;
            ctx.strokeStyle = color;
            ctx.lineWidth = Math.max(1, vRatio);
            ctx.setLineDash(dashed ? [4 * hRatio, 3 * hRatio] : []);
            ctx.globalAlpha = dashed ? 0.7 : 0.95;
            ctx.beginPath();
            ctx.moveTo(left, y);
            ctx.lineTo(left + width, y);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.globalAlpha = 1;
          };

          strokeLevel(yE, self._colors.textMuted, true);
          if (yStop != null) strokeLevel(yStop * vRatio, danger);
          tpYs.forEach((tp) => {
            if (tp.y != null) strokeLevel(tp.y * vRatio, success);
          });

          const ys = [yE];
          if (yStop != null) ys.push(yStop * vRatio);
          tpYs.forEach((tp) => { if (tp.y != null) ys.push(tp.y * vRatio); });
          const top = Math.min(...ys);
          const bottom = Math.max(...ys);
          ctx.strokeStyle = withAlpha(self._colors.textMuted, 0.55);
          ctx.lineWidth = Math.max(1, hRatio);
          ctx.strokeRect(left, top, width, Math.max(1, bottom - top));

          const labelX = right + 6 * hRatio;
          const label = (y, text, color) => {
            if (y == null || !text) return;
            ctx.font = `${Math.round(10 * vRatio)}px sans-serif`;
            ctx.textBaseline = 'middle';
            ctx.textAlign = 'left';
            const padX = 3 * hRatio;
            const tw = ctx.measureText(text).width;
            const th = 11 * vRatio;
            ctx.fillStyle = withAlpha(self._colors.bg || '#0C0F19', 0.82);
            ctx.fillRect(labelX - 1 * hRatio, y - th / 2, tw + padX * 2, th);
            ctx.fillStyle = color;
            ctx.fillText(text, labelX + padX, y);
          };
          label(yE, bracket.pending ? 'Limit' : 'Entry', self._colors.textMuted);
          if (yStop != null) label(yStop * vRatio, 'SL', danger);
          tpYs.forEach((tp) => {
            if (tp.y != null) label(tp.y * vRatio, tp.label, success);
          });
        });
      },
    });

    this._paneView = {
      zOrder: () => 'top',
      update: () => {},
      renderer,
    };
  }

  setBracket(bracket, colors) {
    this._bracket = bracket || null;
    if (colors) {
      this._colors = { ...this._colors, ...colors };
    }
    this._requestUpdate?.();
  }

  attached(param) {
    this._chart = param.chart;
    this._series = param.series;
    this._requestUpdate = param.requestUpdate;
  }

  detached() {
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
  }

  updateAllViews() {
    this._paneView.update();
  }

  paneViews() {
    return [this._paneView];
  }
}

export function fvgBoxesUpToPlayhead(fvgSeries, playhead, extend = 8) {
  if (!Array.isArray(fvgSeries) || playhead < 2) return [];
  const boxes = [];
  const from = Math.max(2, playhead - 80);
  for (let i = from; i <= playhead && i < fvgSeries.length; i++) {
    const g = fvgSeries[i];
    if (!g || !Number.isFinite(g.top) || !Number.isFinite(g.bottom)) continue;
    boxes.push({
      startLogical: g.startIndex ?? i - 2,
      endLogical: Math.min(playhead, (g.endIndex ?? i) + extend),
      topPrice: g.top,
      bottomPrice: g.bottom,
      bullish: g.direction === 'Long',
    });
  }
  return boxes;
}

export function sessionBoxesUpToPlayhead(boxes, playhead, bars = []) {
  if (!Array.isArray(boxes) || playhead < 0) return [];
  return boxes
    .filter((box) => box && box.startIndex <= playhead)
    .map((box) => {
      const endIndex = Math.min(box.endIndex, playhead);
      let high = box.high;
      let low = box.low;
      if (endIndex < box.endIndex && bars.length) {
        high = -Infinity;
        low = Infinity;
        for (let i = box.startIndex; i <= endIndex; i++) {
          const bar = bars[i];
          if (!bar) continue;
          high = Math.max(high, bar.high);
          low = Math.min(low, bar.low);
        }
      }
      return {
        sessionKey: box.sessionKey,
        startLogical: box.startIndex,
        endLogical: endIndex,
        highPrice: high,
        lowPrice: low,
      };
    })
    .filter((box) => Number.isFinite(box.highPrice) && Number.isFinite(box.lowPrice) && box.highPrice >= box.lowPrice);
}

export function exitLabel(exit, takeProfits = []) {
  if (!exit) return 'Exit';
  if (exit.reason === 'tp') {
    const idx = (takeProfits || []).findIndex((tp) => tp.id && tp.id === exit.tpId);
    if (idx >= 0) return `TP ${idx + 1} hit`;
    const match = String(exit.tpId || '').match(/(\d+)/);
    return match ? `TP ${Number(match[1])} hit` : 'TP hit';
  }
  if (exit.reason === 'stop') return 'SL hit';
  return 'Exit';
}

export function executionLabelsFromTrades(trades, bars, playhead, colors = {}) {
  const success = colors.success || '#07CBA3';
  const danger = colors.danger || '#F73263';
  const muted = colors.textMuted || '#7B8A9E';
  const labels = [];
  for (const trade of trades || []) {
    if (trade.entryIndex > playhead) continue;
    const exits = trade.exits || [];
    for (const exit of exits) {
      if (exit.index == null || exit.index > playhead) continue;
      const bar = bars[exit.index];
      if (!bar || !Number.isFinite(exit.price)) continue;
      const color = exit.reason === 'stop' ? danger : exit.reason === 'tp' ? success : muted;
      labels.push({
        time: bar.time,
        price: exit.price,
        text: exitLabel(exit, trade.takeProfits),
        color,
        id: `x-${trade.id}-${exit.index}-${exit.tpId || exit.reason}`,
      });
    }
    if (!exits.length && trade.exitIndex != null && trade.exitIndex <= playhead) {
      const bar = bars[trade.exitIndex];
      if (bar && Number.isFinite(trade.exitPrice)) {
        labels.push({
          time: bar.time,
          price: trade.exitPrice,
          text: exitLabel({ reason: trade.exitReason, tpId: trade.exitReason === 'tp' ? 'tp1' : undefined }, trade.takeProfits),
          color: trade.exitReason === 'stop' ? danger : trade.pnl >= 0 ? success : danger,
          id: `x-${trade.id}`,
        });
      }
    }
  }
  return labels;
}
